import React, { useMemo, useState } from 'react';

import { Popover, Typography, useTheme } from '@mui/material';
import { matchSorter } from 'match-sorter';
import { SearchInput } from 'src/components/SearchInput';
import { useRecentChatThreads } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { ChatEditorHistoryThreadsList } from './ChatEditorHistoryThreadsList';
import { getEditorPopoverStyles, getEditorSearchStyles } from './utils';
import type { Thread } from '../../chat-helpers';
import { groupThreadsByDate } from '../../chat-helpers';
import { usePopoverDimensions } from '../../chat-hooks/usePopoverPosition';
import { EDITOR_PICKER_HEIGHT } from '../AlineMarkdownEditor/utils/styles';

const useStyles = makeStyles()(theme => {
  const sharedSearchStyles = getEditorSearchStyles(theme);
  const sharedPopoverStyles = getEditorPopoverStyles(theme);
  return {
    ...sharedSearchStyles,
    header: {
      ...sharedPopoverStyles.popoverHeader,
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
    },
    headerTitle: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    popoverPaper: {
      ...sharedPopoverStyles.popoverPaper,
      // Width is set dynamically via inline style based on available space
    },
  };
});

export interface ChatEditorHistoryProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onNavigateToThread: (threadId: string) => void;
  /** Fires after a thread is deleted, so the host can leave a thread it was showing. */
  onThreadDeleted: (threadId: UUID) => void;
  open: boolean;
}

export function ChatEditorHistory({
  anchorEl,
  onClose,
  onNavigateToThread,
  onThreadDeleted,
  open,
}: ChatEditorHistoryProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const marginThresholdPx = 8;
  const [searchQuery, setSearchQuery] = useState('');
  const { recentThreads } = useRecentChatThreads({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { maxHeight, openAbove, width } = usePopoverDimensions(anchorEl, {
    defaultHeightPx: EDITOR_PICKER_HEIGHT,
    marginThresholdPx,
  });

  const groupedThreads = useMemo(() => {
    const now = new Date();
    return groupThreadsByDate(now, recentThreads);
  }, [recentThreads]);

  const filteredGroupedThreads = useMemo(() => {
    if (!searchQuery.trim()) return groupedThreads;

    const filterThreads = (threads: Thread[]) =>
      matchSorter(threads, searchQuery, { keys: ['title'] satisfies Array<keyof Thread> });

    return {
      last7DaysThreads: filterThreads(groupedThreads.last7DaysThreads),
      last30DaysThreads: filterThreads(groupedThreads.last30DaysThreads),
      olderThreadsByMonth: Object.entries(groupedThreads.olderThreadsByMonth).reduce<
        Record<string, Thread[]>
      >((acc, [month, threads]) => {
        const filtered = filterThreads(threads);
        if (filtered.length) acc[month] = filtered;
        return acc;
      }, {}),
      todayThreads: filterThreads(groupedThreads.todayThreads),
    };
  }, [groupedThreads, searchQuery]);

  const totalThreads = useMemo(() => {
    return (
      filteredGroupedThreads.todayThreads.length +
      filteredGroupedThreads.last7DaysThreads.length +
      filteredGroupedThreads.last30DaysThreads.length +
      Object.values(filteredGroupedThreads.olderThreadsByMonth).reduce(
        (acc, threads) => acc + threads.length,
        0
      )
    );
  }, [filteredGroupedThreads]);

  const handleThreadClick = (threadId: string) => {
    onNavigateToThread(threadId);
    onClose();
  };

  return (
    <Popover
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'center',
        vertical: openAbove ? 'top' : 'bottom',
      }}
      classes={{ paper: classes.popoverPaper }}
      marginThreshold={marginThresholdPx}
      open={open}
      slotProps={{ paper: { sx: { height: maxHeight, mt: openAbove ? '-4px' : '4px' } } }}
      transformOrigin={{
        horizontal: 'center',
        vertical: openAbove ? 'bottom' : 'top',
      }}
      onClick={e => e.stopPropagation()}
      onClose={onClose}
    >
      <div
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1.5),
          height: '100%',
          maxHeight,
          padding: theme.spacing(1),
          width,
        }}
      >
        <div className={classes.header}>
          <Typography
            className={classes.headerTitle}
            sx={{
              fontWeight: 'bold',
            }}
            variant="body1"
          >
            Chat History
          </Typography>
        </div>
        <SearchInput
          autoFocus
          inputClassName={classes.searchInput}
          minWidth={0}
          placeholder="Search for a chat..."
          search={searchQuery}
          setSearch={setSearchQuery}
        />
        <ChatEditorHistoryThreadsList
          filteredGroupedThreads={filteredGroupedThreads}
          totalThreads={totalThreads}
          onClickThread={handleThreadClick}
          onThreadDeleted={onThreadDeleted}
        />
      </div>
    </Popover>
  );
}
